import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, type Location } from 'react-router';
import type {
  OpportunityCardItem,
  DurationTypeValue,
  InsightType,
  OpportunitiesRoomQueryFilters,
} from '@/entities/opportunity';
import { isOpportunityNew } from '@/entities/opportunity';
import { useOpportunitiesRoom } from '../useOpportunitiesRoom';
import { useOpportunitiesRoomFilters } from '../useOpportunitiesRoomFilters';

export interface OverlayState {
  items: OpportunityCardItem[];
  initialIndex: number;
}

export function useOpportunitiesRoomLayout() {
  const navigate = useNavigate();
  const { state: locationState } = useLocation() as Location<
    { selectedOpportunity: OpportunityCardItem } | undefined
  >;

  const [overlayState, setOverlayState] = useState<OverlayState | null>(null);
  const preloadHandledRef = useRef(false);

  // All filter state lives here so it can feed directly into the server-side
  // query. The toolbar reads `filters` for its inputs; the query reads
  // `queryFilters` (the same values, normalized for the API contract).
  const [opportunityType, setOpportunityType] = useState<DurationTypeValue | null>(null);
  const [radarlistOnly, setRadarlistOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [insightType, setInsightType] = useState<InsightType | null>(null);
  const [market, setMarket] = useState<string | null>(null);
  const [audience, setAudience] = useState<string | null>(null);

  const queryFilters = useMemo((): OpportunitiesRoomQueryFilters | undefined => {
    const hasFilter =
      opportunityType !== null ||
      radarlistOnly ||
      search !== '' ||
      insightType !== null ||
      market !== null ||
      audience !== null;
    if (!hasFilter) return undefined;
    return {
      durationTypes: opportunityType ? [opportunityType] : undefined,
      favoritesOnly: radarlistOnly ? true : undefined,
      insightType: insightType ?? undefined,
      market: market ?? undefined,
      audience: audience ?? undefined,
      search: search || undefined,
    };
  }, [opportunityType, radarlistOnly, search, insightType, market, audience]);

  const {
    opportunities,
    isLoading,
    isKpiLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    kpi,
    toggleFavorite,
  } = useOpportunitiesRoom(queryFilters);

  const { flashOpportunities, otherOpportunities, marketOptions, audienceOptions } =
    useOpportunitiesRoomFilters(opportunities);

  const filters = useMemo(
    () => ({ search, insightType, market, audience }),
    [search, insightType, market, audience],
  );

  const allVisible = useMemo(
    () => [...flashOpportunities, ...otherOpportunities],
    [flashOpportunities, otherOpportunities],
  );

  // Keep the open overlay in step with the list, e.g. a bookmark toggled elsewhere.
  useEffect(() => {
    if (!overlayState) return;

    // Update overlay items with fresh data from allVisible
    const updatedItems = overlayState.items.map((overlayItem) => {
      const freshItem = allVisible.find((o) => o.opportunityId === overlayItem.opportunityId);
      return freshItem ?? overlayItem;
    });

    // Only update if items actually changed (avoid infinite loop)
    const hasChanges = updatedItems.some((item, idx) => {
      const oldItem = overlayState.items[idx];
      return item.isFavorite !== oldItem.isFavorite;
    });

    if (hasChanges) {
      setOverlayState((prev) => (prev ? { ...prev, items: updatedItems } : null));
    }
  }, [allVisible, overlayState]);

  // Open overlay preloaded from route state (e.g. navigated from dashboard)
  useEffect(() => {
    const selected = locationState?.selectedOpportunity;
    if (!selected || preloadHandledRef.current || isLoading) return;

    if (allVisible.length === 0) return;

    preloadHandledRef.current = true;
    const foundIndex = allVisible.findIndex((o) => o.opportunityId === selected.opportunityId);
    // If the selected item isn't in the visible page, prepend it.
    const isInPage = foundIndex !== -1;
    setOverlayState({
      items: isInPage ? allVisible : [selected, ...allVisible],
      initialIndex: isInPage ? foundIndex : 0,
    });

    // Clear route state so navigating back doesn't re-trigger
    void navigate('.', { replace: true, state: null });
  }, [allVisible, locationState, isLoading, navigate]);

  const handleCardClick = useCallback(
    (item: OpportunityCardItem) => {
      const found = allVisible.findIndex((o) => o.opportunityId === item.opportunityId);
      setOverlayState({ items: allVisible, initialIndex: found !== -1 ? found : 0 });
    },
    [allVisible],
  );

  const isNew = useCallback(
    (opp: OpportunityCardItem) => isOpportunityNew(opp.createdAt, kpi.lastVisitedAt),
    [kpi.lastVisitedAt],
  );

  const handleOpenDetail = useCallback(
    (opportunityId: string) => {
      const found = allVisible.findIndex((o) => o.opportunityId === opportunityId);
      if (found === -1) return;
      setOverlayState({ items: allVisible, initialIndex: found });
    },
    [allVisible],
  );

  const openOverlay = useCallback((items: OpportunityCardItem[], initialIndex: number) => {
    setOverlayState({ items, initialIndex });
  }, []);

  const closeOverlay = useCallback(() => {
    setOverlayState(null);
  }, []);

  return {
    // Data
    kpi,
    isLoading,
    isKpiLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    toggleFavorite,
    // Filtered lists
    flashOpportunities,
    otherOpportunities,
    // Toolbar
    filters,
    opportunityType,
    radarlistOnly,
    marketOptions,
    audienceOptions,
    setSearch,
    setInsightType,
    setMarket,
    setAudience,
    setOpportunityType,
    setRadarlistOnly,
    // Helpers
    isNew,
    // Overlay
    overlayState,
    openOverlay,
    closeOverlay,
    handleCardClick,
    handleOpenDetail,
  };
}
